import React, { useEffect, useMemo, useState } from 'react';
import './DomainMergeModal.css';

function getErrorMessage(error) {
  return error?.response?.data?.detail || error?.message || 'Failed to merge domain.';
}

export default function DomainMergeModal({
  sourceDomain,
  deckCount,
  domains,
  onClose,
  onConfirm,
}) {
  const [destination, setDestination] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting, onClose]);

  const filteredDomains = useMemo(() => {
    const query = destination.trim().toLowerCase();
    if (!query) {
      return domains.slice(0, 8);
    }
    return domains
      .filter((domain) => domain.toLowerCase().includes(query))
      .slice(0, 8);
  }, [domains, destination]);

  const trimmedDestination = destination.trim();
  const previewDestination = trimmedDestination || 'the selected domain';

  const handleSubmit = async () => {
    if (!trimmedDestination) {
      setError('Choose or enter a destination domain.');
      return;
    }
    if (trimmedDestination === sourceDomain) {
      setError('Choose a different destination domain.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onConfirm(trimmedDestination);
    } catch (submitError) {
      setError(getErrorMessage(submitError));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="domain-merge-modal-overlay" onClick={() => !isSubmitting && onClose()}>
      <div
        className="domain-merge-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="domain-merge-title"
      >
        <div className="domain-merge-modal-header">
          <div>
            <div className="domain-merge-modal-kicker">DOMAIN MERGE</div>
            <h3 id="domain-merge-title">Merge "{sourceDomain}" ({deckCount} decks) into...</h3>
          </div>
          <button
            className="domain-merge-modal-close"
            onClick={onClose}
            aria-label="Close"
            disabled={isSubmitting}
          >
            &times;
          </button>
        </div>

        <div className="domain-merge-modal-body">
          <label className="domain-merge-modal-label" htmlFor="domain-merge-destination">
            Destination domain
          </label>
          <input
            id="domain-merge-destination"
            className="domain-merge-modal-input"
            type="text"
            value={destination}
            onChange={(event) => {
              setDestination(event.target.value);
              if (error) setError('');
            }}
            placeholder="Type a domain name"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSubmit();
              }
            }}
            autoFocus
            disabled={isSubmitting}
          />

          {filteredDomains.length > 0 && (
            <div className="domain-merge-modal-suggestions">
              {filteredDomains.map((domain) => (
                <button
                  key={domain}
                  type="button"
                  className={`domain-merge-modal-suggestion${trimmedDestination === domain ? ' active' : ''}`}
                  onClick={() => {
                    setDestination(domain);
                    if (error) setError('');
                  }}
                  disabled={isSubmitting}
                >
                  {domain}
                </button>
              ))}
            </div>
          )}

          <div className="domain-merge-modal-preview">
            All {deckCount} decks will move to '{previewDestination}' and inherit its color.
          </div>

          {error && <div className="domain-merge-modal-error">{error}</div>}
        </div>

        <div className="domain-merge-modal-footer">
          <button
            type="button"
            className="domain-merge-modal-btn domain-merge-modal-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="domain-merge-modal-btn domain-merge-modal-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !trimmedDestination}
          >
            {isSubmitting ? 'Merging...' : 'Confirm Merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
